import React, { useEffect, useState } from "react";
import "./App.css";

const ReblocLogoSmall = () => (
  <svg width="100" height="38" viewBox="0 0 1800 700">
    <g transform="translate(120,100)">
      <path d="M0 0 H240 A120 120 0 0 1 240 240 H120 Z" fill="#2E4A3A"/>
      <path d="M0 0 L240 240 L0 500 Z" fill="#123524"/>
      <polygon points="0,260 160,120 160,420" fill="#E7E3D6"/>
      <polygon points="160,420 160,260 360,420" fill="#C49A6C"/>
    </g>
    <text x="700" y="420" fontFamily="Poppins, Arial, sans-serif"
      fontSize="220" fontWeight="700" fill="#1E3B2C">Rebloc</text>
  </svg>
);

function Dashboard() {
  const [bins, setBins]               = useState([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [credits, setCredits]         = useState(0);
  const [sessions, setSessions]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // feedback
  const [feedbackRating,  setFeedbackRating]  = useState(0);
  const [feedbackMsg,     setFeedbackMsg]     = useState("");
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackStatus,  setFeedbackStatus]  = useState("");
  const [showFeedback,    setShowFeedback]    = useState(false);

  const email  = localStorage.getItem("email") || "User";
  const userId = localStorage.getItem("userId");
  const token  = localStorage.getItem("token");

  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      try {
        const [binsRes, userRes, weightsRes] = await Promise.all([
          fetch("https://smart-bin-backend-zygj.onrender.com/api/bins"),
          fetch("https://smart-bin-backend-zygj.onrender.com/api/auth/user/" + userId),
          fetch(
            "https://smart-bin-backend-zygj.onrender.com/api/weights/?userId=" + userId,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        const binsData    = await binsRes.json();
        const userData    = await userRes.json();
        const weightsData = await weightsRes.json();

        setBins(binsData);
        setTotalWeight(userData.totalWeight || 0);
        setCredits(userData.credits || 0);

        if (Array.isArray(weightsData) && weightsData.length > 0) {
          setSessions(weightsData.slice(0, 5));
        } else {
          setSessions((userData.history || []).slice().reverse().slice(0, 5).map(h => ({
            _legacy: true,
            timestamp: h.date,
            weightKg: h.weightKg,
            creditsAwarded: h.credits,
            co2SavedKg: null,
            mfid: null,
          })));
        }

        setLastUpdated(new Date());
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [userId]);

  const handleFeedback = async () => {
    setFeedbackStatus("");
    if (feedbackRating === 0) { setFeedbackStatus("❌ Please select a star rating."); return; }
    if (!feedbackMsg.trim())  { setFeedbackStatus("❌ Please write a message."); return; }
    setFeedbackLoading(true);
    try {
      const res = await fetch(
        "https://smart-bin-backend-zygj.onrender.com/api/feedback",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ rating: feedbackRating, message: feedbackMsg.trim() })
        }
      );
      const data = await res.json();
      if (res.ok) {
        setFeedbackStatus("✅ Thank you for your feedback!");
        setFeedbackRating(0);
        setFeedbackMsg("");
        setShowFeedback(false);
      } else {
        setFeedbackStatus("❌ " + (data.message || "Submission failed."));
      }
    } catch (err) {
      setFeedbackStatus("❌ Network error. Try again.");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleLogout = () => { localStorage.clear(); window.location.href = "/"; };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) +
      " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const co2Saved = (totalWeight * 1.5).toFixed(2);

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-spinner"></div>
      <p>Loading your dashboard...</p>
    </div>
  );

  const bin       = bins.find(b => b.location === "Smart Bin - Anand") || {};
  const fillLevel = bin.fillLevel || 0;
  const fillColor = fillLevel >= 80 ? "#B43232" : fillLevel >= 50 ? "#C49A6C" : "#2E4A3A";

  return (
    <div className="dash-bg">
      <div className="dash-card">

        {/* Header */}
        <div className="dash-header">
          <ReblocLogoSmall />
          <div className="dash-live">
            <span className="live-dot"></span>
            <span>Live</span>
          </div>
        </div>
        <p className="dash-email">{email}</p>

        {/* Credits Hero */}
        <div className="weight-hero">
          <div className="weight-icon">🪙</div>
          <div className="weight-value">
            {typeof credits === "number" ? credits.toFixed(3) : credits}
            <span className="weight-unit"> credits</span>
          </div>
          <div className="weight-label">Your Green Credit Balance</div>
        </div>

        {/* SPB Product */}
        <div className="pickup-section" style={{background:"linear-gradient(135deg,#1E3B2C,#2E4A3A)", borderRadius:"14px", padding:"16px"}}>
          <h3 style={{color:"#C49A6C", fontSize:"13px", fontWeight:"700", letterSpacing:"1px", marginBottom:"6px"}}>🧱 REBLOC SPB PRODUCT</h3>
          <p style={{color:"#E7E3D6", fontSize:"14px", fontWeight:"600", marginBottom:"4px"}}>Silica Plastic Block (SPB)</p>
          <p style={{color:"#aaa", fontSize:"12px", lineHeight:"1.5", marginBottom:"10px"}}>
            Your shredded plastic is converted into SPB construction blocks by Rhino Machines Pvt. Ltd.
            These blocks are used in walls, flooring, and furniture — keeping plastic out of landfills permanently.
          </p>
          <a
            href="https://rebloc-website.vercel.app"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display:"inline-block", background:"#C49A6C", color:"#1E3B2C",
              padding:"8px 16px", borderRadius:"8px", fontSize:"12px",
              fontWeight:"700", textDecoration:"none"
            }}
          >
            Learn More →
          </a>
        </div>

        {/* Stats */}
        <div className="stat-row">
          <div className="stat-card green">
            <div className="stat-icon">⚖️</div>
            <div className="stat-label">Total Recycled</div>
            <div className="stat-value">{totalWeight.toFixed(2)} kg</div>
          </div>
          <div className="stat-card green">
            <div className="stat-icon">🌱</div>
            <div className="stat-label">CO₂ Saved</div>
            <div className="stat-value">{co2Saved} kg</div>
          </div>
        </div>

        {/* Bin Status */}
        <div className="bin-section">
          <div className="bin-header">
            <h3 className="bin-name">📍 Smart Bin — Anand</h3>
            <span className={fillLevel >= 80 ? "bin-status full" : "bin-status normal"}>
              {fillLevel >= 80 ? "Full" : "Normal"}
            </span>
          </div>
          <div className="fill-bar-bg">
            <div className="fill-bar" style={{ width: fillLevel+"%", background: fillColor }}></div>
          </div>
          <p className="fill-text">{fillLevel}% full</p>
        </div>

        {/* Recent Sessions */}
        {sessions.length > 0 && (
          <div className="history-section">
            <h3 className="history-title">Recent Sessions</h3>
            {sessions.map((s, i) => (
              <div key={i} className="history-row" style={{flexDirection:"column", alignItems:"flex-start", gap:"2px"}}>
                <div style={{display:"flex", justifyContent:"space-between", width:"100%"}}>
                  <div className="hist-left">
                    <span className="hist-date">{formatDate(s.timestamp || s.date)}</span>
                    <span className="hist-weight">
                      {(s.weightKg || 0) > 0 ? (s.weightKg).toFixed(3) + " kg" : "—"}
                    </span>
                  </div>
                  <span className="hist-credit">+{(s.creditsAwarded || s.credits || 0).toFixed(3)} credits</span>
                </div>
                {!s._legacy && (
                  <div style={{display:"flex", gap:"12px", fontSize:"11px", color:"#aaa", paddingLeft:"2px"}}>
                    {s.co2SavedKg != null && <span>🌱 {s.co2SavedKg} kg CO₂</span>}
                    {s.mfid && <span>📦 {s.mfid}</span>}
                    {s.materialType && <span>{s.materialType}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {lastUpdated && (
          <p className="last-updated">Updated {lastUpdated.toLocaleTimeString("en-IN")}</p>
        )}

        {/* Feedback Section */}
        <div className="pickup-section">
          <h3 className="history-title">⭐ Share Your Feedback</h3>
          <p className="pickup-desc">How was your experience with the Smart Bin Shredder?</p>
          {!showFeedback ? (
            <button className="pickup-btn" onClick={() => setShowFeedback(true)}>
              Give Feedback
            </button>
          ) : (
            <>
              <div style={{display:"flex", gap:"8px", marginBottom:"12px"}}>
                {[1,2,3,4,5].map(star => (
                  <span
                    key={star}
                    onClick={() => setFeedbackRating(star)}
                    style={{
                      fontSize:"32px",
                      cursor:"pointer",
                      color: star <= feedbackRating ? "#C49A6C" : "#ddd",
                      transition:"color 0.15s"
                    }}
                  >★</span>
                ))}
              </div>
              <textarea
                className="login-input"
                placeholder="Tell us about your experience..."
                value={feedbackMsg}
                onChange={e => setFeedbackMsg(e.target.value)}
                rows={3}
                style={{resize:"vertical", width:"100%", marginBottom:"10px", fontFamily:"inherit"}}
              />
              <div style={{display:"flex", gap:"10px"}}>
                <button className="pickup-btn" onClick={handleFeedback} disabled={feedbackLoading}>
                  {feedbackLoading ? "Submitting..." : "Submit Feedback"}
                </button>
                <button
                  className="pickup-btn"
                  style={{background:"#888"}}
                  onClick={() => { setShowFeedback(false); setFeedbackStatus(""); setFeedbackRating(0); setFeedbackMsg(""); }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
          {feedbackStatus && (
            <p className={feedbackStatus.startsWith("✅") ? "pickup-success" : "pickup-error"}>
              {feedbackStatus}
            </p>
          )}
        </div>

        {/* Website Link */}
        <div className="pickup-section" style={{textAlign:"center"}}>
          <h3 className="history-title">🌐 Learn More About Rebloc</h3>
          <p className="pickup-desc">Visit our website to learn how we are turning plastic waste into a resource.</p>
          <a
            href="https://rebloc-website.vercel.app"
            target="_blank"
            rel="noopener noreferrer"
            className="pickup-btn"
            style={{display:"inline-block", textDecoration:"none", textAlign:"center"}}
          >
            Visit rebloc-website.vercel.app
          </a>
        </div>

        {/* Contact Info */}
        <div className="pickup-section" style={{textAlign:"center"}}>
          <h3 className="history-title">📬 Contact Us</h3>
          <p className="pickup-desc">Have a question or want to know more about Rebloc?</p>
          <a
            href="mailto:bhaktipatel3304@gmail.com"
            style={{color:"#2E4A3A", fontWeight:"600", fontSize:"14px", textDecoration:"none"}}
          >
            ✉️ bhaktipatel3304@gmail.com
          </a>
        </div>

        <button className="logout-btn" onClick={handleLogout}>Logout</button>

      </div>
    </div>
  );
}

export default Dashboard;